import React, { useContext, useMemo } from 'react';
import pick from 'lodash/pick';

import { Modal } from '@motorway/motorway-component-library';

import { VehiclePurchaseModalContentWithoutPermitsChecker } from 'Components/pages/VehicleDetailsRedesign/sections/BiddingSection/PurchaseModal/VehiclePurchaseModalContent/VehiclePurchaseModalContent';
import VisualViewportKeyboardObserver from 'Components/pages/VehicleDetailsRedesign/sections/BiddingSection/PurchaseModal/VisualViewportKeyboardObserver/VisualViewportKeyboardObserver';
import type { VisualViewportKeyboardObserverResultProps } from 'Components/pages/VehicleDetailsRedesign/sections/BiddingSection/PurchaseModal/VisualViewportKeyboardObserver/VisualViewportKeyboardObserver.types';
import { useBrazeProvider } from 'Context/BrazeContext/BrazeContext';
import { useVehicleData } from 'Context/VehicleContext/VehicleDataContext';
import { VehicleInfoContext } from 'Context/VehicleInfoContext';
import VehiclePermitsChecker from 'Controllers/VehiclePermitsChecker/VehiclePermitsChecker';
import { triggerClosePurchaseModalEvent } from 'Services/analytics/events';
import { useTriggerBidPlacedEvent } from 'Services/optimizely/events/useTriggerBidPlacedEvent';
import useSaleTimes from 'Utilities/hooks/useSaleTimes';
import useUser from 'Utilities/hooks/useUser';

import { mapBiddingValuesToLegacyVehicle } from '../mapBiddingValuesToLegacyVehicle';

import type { PurchaseModalBaseProps } from './PurchaseModal.types';

const PurchaseModalBase: React.FC<PurchaseModalBaseProps> = ({
	bidCount,
	isKeyboardOpen,
	isMobile,
	onModalMount,
	permits,
	state,
	userPartners,
	vehicle,
}) => {
	const { isBrazeSDKInitialised } = useBrazeProvider();
	const vehicleInfoContext = useContext(VehicleInfoContext);
	const { todaysSaleEndsAt } = useSaleTimes();
	const triggerBidPlacedEvent = useTriggerBidPlacedEvent();

	const {
		hideVehiclePurchaseModal,
		vehiclePurchaseModalState: { modalType, value },
	} = vehicleInfoContext;

	const onDismissModal = () => {
		triggerClosePurchaseModalEvent();
		hideVehiclePurchaseModal();
	};

	const onSubmit = () => {
		triggerBidPlacedEvent(state.slug);
		hideVehiclePurchaseModal();
	};

	return (
		<Modal dark centered={!isKeyboardOpen} dismissModal={onDismissModal} size="small">
			<VehiclePurchaseModalContentWithoutPermitsChecker
				{...{
					isKeyboardOpen,
					isMobile,
				}}
				bidCount={bidCount}
				isBrazeSDKInitialised={isBrazeSDKInitialised}
				modalType={modalType}
				onDismissModal={onDismissModal}
				onMount={onModalMount}
				onSubmit={onSubmit}
				permits={permits}
				saleEnds={todaysSaleEndsAt}
				userPartners={userPartners}
				value={value}
				vehicle={vehicle}
			/>
		</Modal>
	);
};

const PurchaseModalBaseMemo = React.memo(PurchaseModalBase, (prevProps, nextProps) => {
	const meaningfulProps = ['isKeyboardOpen', 'isMobile', 'onModalMount'];

	const prevMeaningfulProps = Object.values(pick(prevProps, meaningfulProps));
	const nextMeaningfulProps = Object.values(pick(nextProps, meaningfulProps));

	return [prevMeaningfulProps, nextMeaningfulProps].every(Object.is);
});

export const PurchaseModal: React.FC = () => {
	const {
		vehiclePurchaseModalState: { showModal },
	} = useContext(VehicleInfoContext);
	const { bidding, id, sourcePartner, state } = useVehicleData();
	const user = useUser();

	const vehicleForPermitAndModalContent = useMemo(() => {
		return mapBiddingValuesToLegacyVehicle({ biddingVehicleValues: bidding, id, sourcePartner, state });
	}, [bidding, id, sourcePartner, state]);

	const permits = useMemo(() => {
		return new VehiclePermitsChecker(vehicleForPermitAndModalContent);
	}, [vehicleForPermitAndModalContent]);

	if (!showModal) {
		return null;
	}

	return (
		<VisualViewportKeyboardObserver>
			{({ isKeyboardOpen, isMobile, onModalMount }: VisualViewportKeyboardObserverResultProps) => (
				<PurchaseModalBaseMemo
					bidCount={bidding.activeBidCount}
					isKeyboardOpen={isKeyboardOpen}
					isMobile={isMobile}
					onModalMount={onModalMount}
					permits={permits}
					state={state}
					userPartners={user.partners || []}
					vehicle={vehicleForPermitAndModalContent}
				/>
			)}
		</VisualViewportKeyboardObserver>
	);
};
